// An internal link should be recognised by HOST, not by scheme.
//
// The renderer that puts articles on the page decided with
// `href?.startsWith('http')`, which reads "external" and means "absolute". 38 of
// the 522 articles link to gazar.dev's own pages with a full https:// URL, so
// the site opened itself in a new tab, on the surface that carries most of the
// traffic, for a reader the whole objective is about keeping on the site.
//
// Sweeping the corpus would fix today's 38 and guarantee a 39th, because nothing
// stops the next article being written the same way and there is no reason it
// should not be: an absolute URL is what you paste when you copy a link from the
// address bar. So the decision moves to where it belongs.

/**
 * Hosts that are this site.
 *
 * The subdomains are deliberately absent. fa.gazar.dev, hub.gazar.dev and
 * coding.gazar.dev are separate applications with their own navigation, and
 * following one is leaving, which is what a new tab is for. Only the apex and
 * its www alias are the same app.
 */
const SELF_HOSTS = new Set(['gazar.dev', 'www.gazar.dev']);

/**
 * The same destination as a path when it is on this site, otherwise null.
 *
 * Query and fragment are preserved: a link to /courses?tab=free#pricing has to
 * keep meaning that. A bare origin becomes "/" rather than the empty string,
 * which would render an anchor that goes nowhere.
 *
 * Anything unparseable comes back null and is treated as external, because
 * guessing about a URL that does not parse is how a link ends up pointing at the
 * wrong page rather than at an obviously broken one.
 */
export function internalPath(href: string | null | undefined): string | null {
  if (!href) return null;
  const trimmed = href.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (!SELF_HOSTS.has(url.hostname.toLowerCase())) return null;
  return `${url.pathname || '/'}${url.search}${url.hash}`;
}

/**
 * Does following this link leave the site?
 *
 * Root-relative paths, anchors and relative links are all internal, which is
 * unchanged. What changes is that an absolute URL pointing back at gazar.dev is
 * now internal too, which is what it always was.
 */
export function isExternalHref(href: string | null | undefined): boolean {
  if (!href) return false;
  const trimmed = href.trim();
  if (internalPath(trimmed) !== null) return false;
  // mailto: and tel: hand off to another application, so they get the same
  // treatment as another site: a new tab and no referrer.
  return /^(?:https?:|mailto:|tel:|\/\/)/i.test(trimmed);
}

/**
 * The href to actually render: absolute self-links collapsed to a path,
 * everything else untouched.
 *
 * Rewriting rather than only dropping target="_blank" is the part that matters
 * for a single-page app. A same-origin absolute URL still costs a full document
 * load; the path does not, once anything on the page is intercepting clicks.
 */
export function renderHref(href: string | null | undefined): string {
  if (!href) return '';
  return internalPath(href) ?? href.trim();
}
